import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { DashboardLayout } from "../components/DashboardLayout";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Session } from "@supabase/supabase-js";
import { Toaster } from "sonner";

import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-semibold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Lovable App" },
      { name: "description", content: "Studio Tattoo is a management system for tattoo studios, offering client CRM, scheduling, financial tracking, inventory, and dashboard." },
      { name: "author", content: "Lovable" },
      { property: "og:title", content: "Lovable App" },
      { property: "og:description", content: "Studio Tattoo is a management system for tattoo studios, offering client CRM, scheduling, financial tracking, inventory, and dashboard." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "twitter:title", content: "Lovable App" },
      { name: "twitter:description", content: "Studio Tattoo is a management system for tattoo studios, offering client CRM, scheduling, financial tracking, inventory, and dashboard." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/1c4d19cf-5ea7-41f5-953b-62b511b47793/id-preview-9cc22291--3be983e2-b649-4469-80e0-d8a782bfc72b.lovable.app-1778810850874.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/1c4d19cf-5ea7-41f5-953b-62b511b47793/id-preview-9cc22291--3be983e2-b649-4469-80e0-d8a782bfc72b.lovable.app-1778810850874.png" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}


function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<{ ativo: boolean; texto: string } | null>(null);

  const AUTH_PATHS = ["/login", "/cadastro", "/redefinir-senha"];
  const isAuthPage = AUTH_PATHS.includes(location.pathname);
  // Ficha pública de cadastro/anamnese: acessível sem login, inclusive por quem já
  // está logado (ex: tatuador testando o próprio link) — não entra no guard de sessão.
  const isFichaPage = location.pathname.startsWith("/ficha/");
  const isPublicPage = isAuthPage || isFichaPage;
  // Na página de redefinição a sessão é temporária (link do e-mail) — não redirecionar pra home.
  const isResetPage = location.pathname === "/redefinir-senha";

  useEffect(() => {
    if (isFichaPage) {
      setLoading(false);
      return;
    }

    const checkSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);

        if (!currentSession && !isAuthPage) {
          navigate({ to: "/login", replace: true });
        } else if (currentSession && isAuthPage && !isResetPage) {
          navigate({ to: "/", replace: true });
        }

        if (currentSession && !isAuthPage) {
          const { data: config } = await supabase
            .from("admin_config")
            .select("banner_ativo, banner_texto")
            .single();
          if (config) setBanner({ ativo: config.banner_ativo, texto: config.banner_texto });
        }
      } catch (err) {
        console.error("Session check error:", err);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: any, newSession: Session | null) => {
      setSession(newSession);

      if (!newSession && !isAuthPage) {
        navigate({ to: "/login", replace: true });
      } else if (newSession && isAuthPage && !isResetPage) {
        navigate({ to: "/", replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [location.pathname, navigate, isAuthPage, isResetPage, isFichaPage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="size-12 rounded-full border-4 border-muted border-t-primary animate-spin" />
      </div>
    );
  }

  const showBanner = !isPublicPage && banner?.ativo && banner?.texto;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {showBanner && (
        <div className="w-full bg-amber-500 text-amber-950 text-sm font-semibold text-center px-4 py-2.5 z-50 shrink-0">
          📢 {banner.texto}
        </div>
      )}
      {isPublicPage ? <Outlet /> : <DashboardLayout />}
      {/* montado aqui (não dentro do DashboardLayout) para funcionar também em
          login/cadastro/redefinir-senha/ficha, que não usam esse layout */}
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
}
